// PaymentReceived — PayPal returns here after subscription approval with
// ?subscription_id=… . If the buyer is signed in (the normal flow — checkout
// requires login), the membership activates AUTOMATICALLY right here.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Rocket, AlertTriangle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export default function PaymentReceived() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const subscriptionId = searchParams.get("subscription_id");
  const [state, setState] = useState<"activating" | "done" | "error" | "no-sub">(
    subscriptionId ? "activating" : "no-sub",
  );
  const [result, setResult] = useState<{ plan?: string; credits?: number | string } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!subscriptionId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("verify-square-payment", {
          body: { subscription_id: subscriptionId },
        });
        if (cancelled) return;
        if (error || !data?.success) throw new Error(data?.error || error?.message || "Activation failed");
        setResult({ plan: data.plan, credits: data.credits_added ?? data.credits_remaining });
        setState("done");
      } catch (e: any) {
        if (!cancelled) { setErrorMsg(e.message); setState("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [subscriptionId, user]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl rounded-2xl overflow-hidden border-2 border-slate-800 shadow-xl bg-white">
        <div className="bg-slate-900 px-8 py-7 text-center">
          <div className={`w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center ${state === "error" ? "bg-amber-500" : "bg-emerald-500"}`}>
            {state === "activating" && user ? <Loader2 className="w-8 h-8 text-white animate-spin" /> :
             state === "error" ? <AlertTriangle className="w-8 h-8 text-white" /> :
             <CheckCircle2 className="w-8 h-8 text-white" />}
          </div>
          <h1 className="text-white font-extrabold text-2xl tracking-tight">
            {state === "done" ? "You're activated!" : "Payment received!"}
          </h1>
          <p className="text-white/80 text-[15px] mt-1">
            {state === "done"
              ? `Your ${result?.plan ?? ""} membership is live${result?.credits ? ` — ${result.credits} credits are on your account` : ""}.`
              : "Thank you for subscribing to TailorWiz."}
          </p>
        </div>
        <div className="px-8 py-7 text-center">
          {state === "activating" && user && (
            <p className="text-[14px] text-slate-600">Activating your membership automatically — a few seconds…</p>
          )}
          {state === "activating" && !user && (
            <>
              <p className="text-[14px] text-slate-600 mb-5">
                Sign in and your membership activates automatically.
              </p>
              <Link to={`/auth`}>
                <button className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white font-extrabold py-3.5 rounded-xl text-[16px] shadow-lg">
                  Sign In to Activate
                </button>
              </Link>
            </>
          )}
          {state === "done" && (
            <Link to="/">
              <button className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-extrabold py-3.5 rounded-xl text-[16px] shadow-lg">
                <Rocket className="w-4 h-4" /> Start Tailoring
              </button>
            </Link>
          )}
          {state === "error" && (
            <>
              <p className="text-[14px] text-slate-600 mb-4">
                {errorMsg} — if you just paid, wait a few seconds and refresh this page.
                Still stuck? <Link to="/contact" className="text-violet-600 font-bold hover:underline">Contact us</Link>.
              </p>
              <button onClick={() => window.location.reload()} className="w-full border-2 border-slate-300 hover:border-violet-600 text-slate-700 font-bold py-3 rounded-xl text-[15px]">
                Try Again
              </button>
            </>
          )}
          {state === "no-sub" && (
            <p className="text-[14px] text-slate-600">
              Your payment is in. If your membership isn't active within a few minutes,{" "}
              <Link to="/contact" className="text-violet-600 font-bold hover:underline">contact us</Link>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
